import {useState} from 'preact/hooks'
import {
    FilterOptions, SearchFilters, TooltipTextsProps, FilterItem, PrefillFiltersProps,
    KuratierteReiseItem, UpdateFilterItem, ExtIdItem, SearchRedirect
} from '../types/travel'
import {PriceSlider} from './PriceSlider'
import {DurationPicker} from './DurationPicker'
import {DestinationSearchV2} from './DestinationSearchV2'
import {FilterSection} from "./FilterSection.tsx";
import {TravelDateSelector} from "./TravelDateSelector.tsx";
import {IconChevron} from "./svg-icons/IconChevron.tsx";
import {getSectionVisibilityClass} from "../utils/htmlUtils.ts";

interface FilterSidebarProps {
    filters: SearchFilters
    onFiltersChange: (filters: SearchFilters) => void
    filterOptions: FilterOptions
    toolTipTexts?: TooltipTextsProps
    prefillFilters?: PrefillFiltersProps
    kuratierteReisen?: KuratierteReiseItem[]
    gridViewState: boolean
    setGridViewState: (value: boolean) => void
    extIdItems?: ExtIdItem[]
    searchRedirects?: SearchRedirect[];
}

export function FilterSidebar({
                                  filters,
                                  onFiltersChange,
                                  filterOptions,
                                  toolTipTexts,
                                  prefillFilters,
                                  gridViewState,
                                  setGridViewState,
                                  kuratierteReisen,
                                  extIdItems,
                                  searchRedirects,
                                }
                              : FilterSidebarProps) {

    const [isOpenShowMoreFilters, setIsOpenShowMoreFilters] = useState(true);

    const handleShowMoreFilters = (current : boolean) => {
        setIsOpenShowMoreFilters(!current)
    }

    const updateFilter = (category: keyof SearchFilters, value: string, checked: boolean, override: boolean = false) => {
        const current = (filters[category] as string[]) || []
        let updated = checked
            ? [...current, value]
            : current.filter(v => v !== value)

        if(override) {

        }

        onFiltersChange({
            ...filters,
            [category]: updated
        })
    }

    const updateFilterArray = (category: keyof SearchFilters, values: string[]) => {
        onFiltersChange({
            ...filters,
            [category]: values
        })
    }

    const updateMultiFilterArray = (updateFilterItems: UpdateFilterItem[]) => {
        const updatedFilters = { ...filters };

        updateFilterItems.forEach(({ category, values }) => {
            if (category === 'start' || category === 'end') {
                // These fields expect string, not string[]
                updatedFilters[category] = Array.isArray(values) ? values[0] : values;
            } else {
                // Other fields expect string[]
                updatedFilters[category] = Array.isArray(values) ? values : [values];
            }
        });

        onFiltersChange(updatedFilters);
    }

    function enrichWithTooltips(
        tooltipTexts: TooltipTextsProps,
        filterOptions: FilterOptions
    ): FilterOptions {
        // nur shallow copy, Arrays werden noch referenziert
        const result: FilterOptions = { ...filterOptions };

        for (const [category, tooltips] of Object.entries(tooltipTexts)) {
            // @ts-ignore
            const items = result[category];
            if (Array.isArray(items)) {
                items.forEach((item: FilterItem) => {
                    const tooltip = tooltips[item.value as string];
                    if (tooltip) {
                        item.tooltip = tooltip;
                    }
                });
            }
        }

        return result;
    }

    const enrichedFilterOptions = toolTipTexts
        ? enrichWithTooltips(toolTipTexts, filterOptions)
        : filterOptions;

    return (
        <div className="w-full h-fit">
            {/* Suchfelder */}
            <div className={getSectionVisibilityClass('destination', prefillFilters)}>
                <DestinationSearchV2
                    options={filterOptions.destination}
                    cssClasses={"mb-5"}
                    selectedValues={filters.destination || []}
                    kuratierteReisen={kuratierteReisen || []}
                    searchRedirects={searchRedirects || []}
                    onSelectionChange={(values) => {
                        onFiltersChange({
                            ...filters,
                            destination: values
                        })
                    }}
                    extIdItems={extIdItems}
                />
            </div>

            <TravelDateSelector
                gridViewState={gridViewState}
                setGridViewState={setGridViewState}
                options={filterOptions.yearmonth || []}
                selectedValues={filters.yearmonth || []}
                onSelectionChange={updateFilterArray}
                onDateSelection={updateMultiFilterArray}
                initialStart={filters.start}
                initialEnd={filters.end}
                cssClasses={"mb-9"} />

            <div className={getSectionVisibilityClass('reiseart', prefillFilters)}>
                <FilterSection
                    title="Reiseart"
                    titleToolTip={toolTipTexts?.reiseart?.parent}
                    section="reiseart"
                    options={enrichedFilterOptions.reiseart || []}
                    filters={filters}
                    onFilterUpdate={updateFilter}
                />
            </div>

            <FilterSection
                title="Schwierigkeitsgrade"
                titleToolTip={toolTipTexts?.difficulty?.parent}
                section="difficulty"
                options={enrichedFilterOptions.difficulty || []}
                filters={filters}
                onFilterUpdate={updateFilter}
            />

            <PriceSlider
                minPrice={filterOptions.priceRange?.min || 0}
                maxPrice={filterOptions.priceRange?.max || 20000}
                onPriceChange={(min, max) => {
                    onFiltersChange({
                        ...filters,
                        price: [`${min}-${max}`]
                    })
                }}
            />

            <DurationPicker
                options={filterOptions.duration || []}
                selectedValues={filters.duration}
                onDurationChange={updateFilter}
            />

            <h2>
                <button className="w-full flex justify-between items-center" onClick={() => handleShowMoreFilters(isOpenShowMoreFilters)}>
                    Weitere Filter <span><IconChevron cssClasses={isOpenShowMoreFilters ? 'rotate-0' : 'rotate-180'} /></span>
                </button>
            </h2>

            {isOpenShowMoreFilters && (
                <>
                    <div className={getSectionVisibilityClass('anreiseart', prefillFilters)}>
                        <FilterSection
                            title="Anreise"
                            titleToolTip={toolTipTexts?.anreiseart?.parent}
                            section="anreiseart"
                            options={enrichedFilterOptions.anreiseart || []}
                            filters={filters}
                            onFilterUpdate={updateFilter}
                        />
                    </div>

                    <div className={getSectionVisibilityClass('unterkunft', prefillFilters)}>
                        <FilterSection
                            title="Unterkunftstyp"
                            titleToolTip={toolTipTexts?.unterkunft?.parent}
                            section="unterkunft"
                            options={enrichedFilterOptions.unterkunft || []}
                            filters={filters}
                            onFilterUpdate={updateFilter}
                        />
                    </div>

                    <FilterSection
                        title="Veranstalter"
                        section="veranstalter"
                        titleToolTip={toolTipTexts?.veranstalter?.parent}
                        options={enrichedFilterOptions.veranstalter || []}
                        filters={filters}
                        onFilterUpdate={updateFilter}
                    />

                    <FilterSection
                        title="Standortwechsel"
                        section="standortwechsel"
                        titleToolTip={toolTipTexts?.standortwechsel?.parent}
                        options={enrichedFilterOptions.standortwechsel || []}
                        filters={filters}
                        onFilterUpdate={updateFilter}
                    />

                    <FilterSection
                        title="Empfehlungen"
                        section="empfehlung"
                        titleToolTip={toolTipTexts?.empfehlung?.parent}
                        options={enrichedFilterOptions.empfehlung || []}
                        filters={filters}
                        onFilterUpdate={updateFilter}
                    />

                    <div className={getSectionVisibilityClass('interesse', prefillFilters)}>
                        <FilterSection
                            title="Interessen"
                            section="interesse"
                            titleToolTip={toolTipTexts?.interesse?.parent}
                            options={enrichedFilterOptions.interesse || []}
                            filters={filters}
                            onFilterUpdate={updateFilter}
                            cssClasses={"border-0!"}
                        />
                    </div>

                </>
            )}

        </div>
    )
}
